import convert from "convert-units";
import get from "lodash/get";
import round from "lodash/round";
import set from "lodash/set";

function updateUnits(obj, preferred) {
  if (!("_units" in obj) || get(obj, "modal.type", false) === "add-exercise")
    return;
  const conversions = obj._units;
  delete obj._units;

  // Go over units and give them the measurement system based off of user preferred unit.
  Object.keys(conversions).forEach((key) => {
    const conversion = conversions[key];
    if (!conversion || !conversion.type) return;

    const { type, imperial, metric } = conversion;
    const from = type === "metric" ? metric : imperial;
    const to = type === "metric" ? imperial : metric;
    const value = get(obj, key);

    // If we switched from the preferred measurement system then we update the inputs.
    if (process.browser) {
      // Find input and update its value.
      const input = document.querySelector(`[data-name="${key}"]`);

      if (input) {
        if (type !== preferred) {
          // Find toggle and switch to back to preferred option.
          const toggle = document.querySelector(
            `[name="_units[${key}][type]"][value="${preferred}"]`
          );
          if (toggle) toggle.click();
          // Find input and convert to prefered units.
          input.value = convertUnit(value, from, to);
        }

        // Ensure properly rounded inputs.
        input.value = round(input.value, 2);
      }
    }

    // Update all data in obj to metric for api submission.
    if (from !== metric) set(obj, key, convertUnit(value, from, metric));
  });

  return obj;
}

export function convertUnit(value, from, to) {
  if (value == null || value === "") return "";
  if (from === to) return value;
  return convert(value).from(from).to(to);
}
export const getBodywweightAndUnit = (bodyweight, userPreference) => {
  if (!bodyweight) return "-";
  if (userPreference === "imperial")
    return `${round(convertUnit(bodyweight, "kg", "lb"), 1)} Lb`;
  return `${round(bodyweight, 1)} Kg`;
};

// Extracted from DailyGoals.js's local `formatWater` - liters are the only
// storage unit (client-goals schema), oz conversion is manual (not via
// `convertUnit`/the `convert-units` package, which DailyGoals.js never used
// for this either) since the package has no built-in liter<->fluid-oz path.
// Metric-check with an imperial default (not the other way around) to match
// `formatWater`'s original convention - same as this same file's bodyweight
// formatting in DailyGoals.js, which imperial-defaults on any non-"metric"
// `units` value (legacy users can have `units` missing entirely: the user
// schema's Joi default only applies to freshly-validated payloads, it does
// not backfill existing documents).
const OZ_PER_L = 33.8140227;
export const getWaterIntakeAndUnit = (liters, userPreference) => {
  if (liters == null) return null;
  return userPreference === "metric"
    ? `${round(liters, 1)} L`
    : `${round(liters * OZ_PER_L, 0)} oz`;
};

// Extracted from ClientGoals.js's inline PR-load conversion, now also used
// by usePhaseSummaryData for the Next Milestone chip - one source of truth
// so a PR's load never renders differently between the two surfaces.
// Metric-check convention (like getWaterIntakeAndUnit), matching
// ClientGoals.js's original behavior exactly. Returns the parts separately
// (not a single string like getBodywweightAndUnit/getWaterIntakeAndUnit)
// since consumers format them differently - ClientGoals.js shows "value
// unit", the milestone chip shows only the value.
export const getLoadAndUnit = (load, userPreference) => ({
  value:
    userPreference === "metric"
      ? round(load || 0, 2)
      : round(convertUnit(load || 0, "kg", "lb"), 2),
  unit: userPreference === "metric" ? "Kg" : "Lbs",
});

// Same kg -> displayed-units conversion getBodywweightAndUnit/getLoadAndUnit
// already do, but returning a bare number instead of a formatted "value
// unit" string - for prefilling (and comparing against, to detect a real
// edit) an editable input field, where a unit suffix doesn't belong.
// `decimals` defaults to 2 (matches getLoadAndUnit's own rounding); pass 1
// for bodyweight to match getBodywweightAndUnit's instead, so an edit form
// never shows more precision than its own read-only row already displayed.
export const getEditableValue = (rawKg, userPreference, decimals = 2) =>
  userPreference === "metric"
    ? round(rawKg || 0, decimals)
    : round(convertUnit(rawKg || 0, "kg", "lb"), decimals);

export default updateUnits;
